import { VdFeatureManager } from '@vend/vend.js'
import angular, { IDeferred, IPromise } from 'angular'
import map from 'lodash/map'
import { trackError } from '@vend/utilities'
import { SyncInterruptedError } from '../../constants'
import { channelsStore } from '../../factories/database/channels-store'
import { customerGroupsStore } from '../../factories/database/customer-groups-store'
import { outletsStore } from '../../factories/database/outlets-store'
import { paymentTypesStore } from '../../factories/database/payment-types-store'
import { priceBookProductsStore } from '../../factories/database/price-book-products-store'
import { priceBooksStore } from '../../factories/database/price-books-store'
import { productTaxesStore } from '../../factories/database/product-taxes-store'
import { quickKeyLayoutsStore } from '../../factories/database/quick-key-layouts-store'
import { registersStore } from '../../factories/database/registers-store'
import { EntityStore } from '../../factories/database/entity-store'

import { $injector, $log, $q } from '../../ng-compat/imports'
import { EventEmitter } from '../../util/event-emitter'
import { PosStateManager } from '../pos-state-manager'

const entityStores: Record<string, EntityStore<$TSFixMe>> = {
  channelsStore,
  customerGroupsStore,
  outletsStore,
  paymentTypesStore,
  priceBookProductsStore,
  priceBooksStore,
  productTaxesStore,
  quickKeyLayoutsStore,
  registersStore,
}

interface EntitySyncConfig {
  entityStoreName: string
  data: {}
  deferred: IDeferred<unknown>
  promise: IPromise<unknown>
  outletId?: string | IDeferred<string>
  interrupt: boolean
  syncComplete: boolean
}

type Events = {
  entitySync: [EntitySyncConfig]
  entitySyncComplete: [EntitySyncConfig]
  entitySyncPendingOutletId: [EntitySyncConfig]
  entitySyncTriggered: [EntitySyncConfig]
}

/**
 * Provides common logic and flow for syncing entities with the server.
 *
 * @module AbstractSyncManager
 */
angular
  .module('webregister.managers')
  .service('AbstractSyncManager', AbstractSyncManagerFactory)

function AbstractSyncManagerFactory(
  posStateManager: PosStateManager,
  vdFeatureManager: VdFeatureManager
) {
  /**
   * Provides common logic and flow for syncing entities with the server.
   *
   * @class AbstractSyncManager
   * @extends EventEmitter
   * @constructor
   *
   * @param config {Object}
   *
   *        @param config.managedEntityStores {Array<String>}
   *               Array of entity store names for the entities managed by this sync manager instance
   */
  class AbstractSyncManager extends EventEmitter<Events> {
    private _managedEntityStores: string[]
    private _resyncOnOutletIdChange: $TSFixMe
    constructor(config: $TSFixMe) {
      super()

      this._managedEntityStores = config.managedEntityStores
      this._resyncOnOutletIdChange = []
    }

    // ============================== Public API ============================== //

    /**
     * Initialises the sync manager and any related database stores etc.
     *
     * @method init
     *
     * @return {Promise} A promise resolved with the sync manager instance once initialisation is complete
     */
    public init() {
      return $q
        .all([
          ...map(this._managedEntityStores, this._initEntity.bind(this)),
          vdFeatureManager.init(),
        ])
        .then(() => this._runEntitiesPostInit())
    }

    /**
     * Perform a sync of the entities managed by this sync manager.
     *
     * @method sync
     *
     * @return {Promise} A promise resolved once the sync has completed
     */
    public sync() {
      return this._getEntitiesToSync().then(this._syncEntities.bind(this))
    }

    /**
     * Get the list of entities managed by this sync manager that require resyncing when the outlet ID changes.
     *
     * @method getEntitiesToResyncOnOutletIdChange
     *
     * @return {Array<String>} Array of store names for entities that should be resynced on outlet ID change
     */
    public getEntitiesToResyncOnOutletIdChange() {
      return this._resyncOnOutletIdChange
    }

    /**
     * Prepare the entity with the given store name for resync.
     *
     * @method prepareEntityForResync
     *
     * @param entityStoreName {String}
     *        The entity store name of the entity to prepare for resync
     *
     * @return {Promise} A promise resolved once the entity is ready for resync
     */
    public prepareEntityForResync(/* entityStoreName */) {
      // Default to doing nothing
      return $q.when()
    }

    // ============================== Protected API ============================== //
    private _getEntityStore(entityStoreName: string): EntityStore<$TSFixMe> {
      const injectableName = `${entityStoreName}Store`
      const entityStore = entityStores[injectableName]
      if (entityStore) {
        return entityStore
      }
      return $injector.get(injectableName)
    }

    /**
     * Initialise an entity specified by the given store name in preparation for syncing it.
     *
     * @protected
     * @method _initEntity
     *
     * @param entityStoreName {String}
     *        Name of the entity store for the entity to initialise
     *
     * @return {Promise} A promise resolved once the entity has been initialised for syncing
     */
    protected _initEntity(entityStoreName: string) {
      const entityStore = this._getEntityStore(entityStoreName)
      if (entityStore.filterByOutlet) {
        this._resyncOnOutletIdChange.push(entityStoreName)
      }
      return this._initEntityStore(entityStore)
    }

    /**
     * Get the list EntitySyncConfigs for the entities to sync.
     *
     * @protected
     * @method _getEntitiesToSync
     *
     * @return {Promise<Array<EntitySyncConfig>>} Promise resolved with the array of EntitySyncConfigs for the
     *         entities to sync
     */
    protected _getEntitiesToSync() {
      const entitiesToSync = this._managedEntityStores.filter(storeName => {
        const entity = this._getEntityStore(storeName)
        return entity.shouldBeSynchronized()
      })

      return $q.when(map(entitiesToSync, this._getEntitySyncConfig.bind(this)))
    }

    /**
     * Get a new EntitySyncConfig for the given entity.
     *
     * @protected
     * @method _getEntitySyncConfig
     *
     * @param entityStoreName {String}
     *        Entity store name of the entity to get the sync config for
     *
     * @return {EntitySyncConfig} The sync config for the given entity
     */
    protected _getEntitySyncConfig(entityStoreName: string) {
      const deferred = $q.defer()
      const entitySyncConfig = {
        entityStoreName,
        data: {},
        deferred,
        promise: deferred.promise,
        interrupt: false,
        syncComplete: false,
      }
      this.emit('entitySyncTriggered', entitySyncConfig)
      return entitySyncConfig
    }

    /**
     * Sync the given entities.
     *
     * @protected
     * @method _syncEntities
     *
     * @param entitySyncConfigs {Array<EntitySyncConfig>}
     *        Array of EntitySyncConfigs for the entities to sync
     *
     * @return {Promise} A promise resolved with the array of results of syncing the given entities
     */
    protected _syncEntities(entitySyncConfigs: EntitySyncConfig[]) {
      return $q.all(
        map(entitySyncConfigs, entityConfig => this._syncEntity(entityConfig))
      )
    }

    /**
     * Sync the given entity
     *
     * @protected
     * @method _syncEntity
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        Sync config for the entity to sync
     *
     * @return {Promise} A promise resolved with the result of syncing the given entity
     */
    protected _syncEntity(
      entitySyncConfig: EntitySyncConfig
    ): IPromise<$TSFixMe> {
      const entityStoreName = entitySyncConfig.entityStoreName
      const entityStore = this._getEntityStore(entityStoreName)
      let outletId
      if (entityStore.filterByOutlet) {
        outletId = posStateManager.getOutletId()

        if (outletId) {
          entitySyncConfig.outletId = outletId
        } else {
          return this._waitForOutletId(entitySyncConfig).then(
            this._syncEntity.bind(this, entitySyncConfig)
          )
        }
      }

      $log.debug('[SyncManager] SYNCING entity store "' + entityStoreName + '"')

      const syncPromise = $q
        .when(this._doSyncEntity(entitySyncConfig))
        .then(this._completeOrContinueSync.bind(this))

      entitySyncConfig.deferred.resolve(syncPromise)

      this.emit('entitySync', entitySyncConfig)

      return syncPromise
    }

    /**
     * Either completes (logging, firing events, etc) or continues (requests another page of data from the
     * server) the sync of the current entity based on the `syncComplete` flag in the passed EntitySyncConfig.
     *
     * @protected
     * @method _completeOrContinueSync
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        Config for the current entity being synced
     *
     * @return {Any|Promise} Either the result of the sync if the sync has completed or a Promise resolved once
     *         the sync has complete if the sync continues
     */
    protected _completeOrContinueSync(entitySyncConfig: EntitySyncConfig) {
      const entityStoreName = entitySyncConfig.entityStoreName

      if (entitySyncConfig.interrupt) {
        $log.debug(
          '[SyncManager] SYNC INTERRUPTED for entity store "' +
            entityStoreName +
            '"'
        )
        return $q.reject(new SyncInterruptedError())
      } else if (entitySyncConfig.syncComplete) {
        $log.debug(
          '[SyncManager] SYNC COMPLETE for entity store "' +
            entityStoreName +
            '"'
        )

        try {
          this.emit('entitySyncComplete', entitySyncConfig)
        } catch (error) {
          trackError(
            '[AbstractSyncManager] Error in entitySyncComplete event',
            { data: { error, entitySyncConfig } }
          )
        }
      } else {
        return this._syncEntity(entitySyncConfig)
      }

      return this._getSyncResult(entitySyncConfig)
    }

    /**
     * Suspend syncing of the specified entity until the user selects an outlet and register. Emits the
     * `entitySyncPendingOutletId` event to notify the overarching sync manager to resume the sync when the
     * outlet ID is set.
     *
     * @protected
     * @method _waitForOutletId
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        The EntitySyncConfig for the entity being synced
     *
     * @return {Promise} The promise that will be resolved when the outlet ID has been set
     */
    protected _waitForOutletId(entitySyncConfig: EntitySyncConfig) {
      const deferredOutletId = $q.defer<string>()

      entitySyncConfig.outletId = deferredOutletId

      this.emit('entitySyncPendingOutletId', entitySyncConfig)

      return deferredOutletId.promise
    }

    // ============================== Abstract / Override API ============================== //

    /**
     * Initialise a given entity store in preparation for syncing related entities.
     *
     * @protected
     * @method _initEntityStore
     *
     * @param entityStore {EntityStore}
     *        The entity store to initialise in preparation for syncing related entities
     *
     * @return {Promise} A promise resolved when the given entity store has been initialised for sync
     */
    protected _initEntityStore(entityStore: EntityStore<$TSFixMe>) {
      return $q.when()
    }

    /**
     * Performs a single iteration for the sync of the entity specified by the given EntitySyncConfig.
     *
     * @protected
     * @method _doSyncEntity
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        The EntitySyncConfig for the entity to sync
     *
     * @return {Promise<EntitySyncConfig>} A promise resolved with the updated EntitySyncConfig when a single
     *         iteration of the sync is complete
     */
    protected _doSyncEntity(entitySyncConfig: $TSFixMe) {
      return $q.reject('Unexpected: _doSyncEntity method should be overridden')
    }

    /**
     * Get the result of a completed sync (to resolve the promise with) given the EntitySyncConfig.
     *
     * @protected
     * @method _getSyncResult
     *
     * @param entitySyncConfig {EntitySyncConfig}
     *        The config for the sync of this entity
     *
     * @return {Any} The result of the sync of this entity
     */
    protected _getSyncResult(entitySyncConfig: EntitySyncConfig) {
      return entitySyncConfig.data
    }

    /**
     * Runs each store postInitProcess method
     *
     * @protected
     * @method _runEntitiesPostInit
     *
     * @return {IPromise<any[]>} IPromise<any[]>
     */
    protected _runEntitiesPostInit() {
      const entities = this._managedEntityStores.map(this._getEntityStore)
      return $q.all(entities.map(entity => entity.postInitProcess()))
    }
  }

  return AbstractSyncManager
}

// ======================================== EntitySyncConfig class ======================================== //

// This class definition and subsequent properties define the object structure for the config object passed around
// when syncing a particular entity for additional clarity and the option of referencing it as a type in method docs
// (e.g. {EntitySyncConfig}).

/**
 * State and data for the current sync of an entity.
 *
 * @private
 * @static
 * @class EntitySyncConfig
 */

/**
 * Name of the EntityStore associated with this entity.
 *
 * @property entityStoreName
 * @type String
 */

/**
 * Data associated with the current sync, varies depending on the sync manager implementation.
 *
 * @property data
 * @type Object
 */

/**
 * Promise that will be resolved when the current sync of this entity completes.
 *
 * @property promise
 * @type Promise
 */

/**
 * Flag indicating whether the current sync of this entity should be interrupted at the next opportune moment.
 *
 * @property interrupt
 * @type Boolean
 */

/**
 * Flag indicating whether the current sync of this entity is complete or whether the sync should be continued.
 *
 * @property syncComplete
 * @type Boolean
 */

/**
 * Either the current outlet ID or a deferred that should be resolved with the outlet ID when it becomes available.
 *
 * @property outletId
 * @type String|Deferred
 */
